import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Shield, LogOut, Check, ThumbsUp, ThumbsDown, Mail, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { API } from "../lib/api";

const AdminReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminKey, setAdminKey] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState("reviews");

  const fetchData = async () => {
    try {
      const [reviewsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/reviews/pending?admin_key=${adminKey}`),
        axios.get(`${API}/contact/messages?admin_key=${adminKey}`)
      ]);
      setReviews(reviewsRes.data.reviews);
      setMessages(messagesRes.data.messages);
      setAuthenticated(true);
    } catch (error) {
      toast.error("Invalid admin key");
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reviewId) => {
    try {
      await axios.put(`${API}/reviews/${reviewId}/approve?admin_key=${adminKey}`);
      setReviews(reviews.filter(r => r.id !== reviewId));
      toast.success("Review approved!");
    } catch (error) {
      toast.error("Failed to approve review");
    }
  };

  const handleReject = async (reviewId) => {
    try {
      await axios.put(`${API}/reviews/${reviewId}/reject?admin_key=${adminKey}`);
      setReviews(reviews.filter(r => r.id !== reviewId));
      toast.success("Review rejected");
    } catch (error) {
      toast.error("Failed to reject review");
    }
  };

  const handleMarkRead = async (messageId) => {
    try {
      await axios.put(`${API}/contact/${messageId}/read?admin_key=${adminKey}`);
      setMessages(messages.map(m => m.id === messageId ? { ...m, read: true } : m));
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#F0EEEB] flex items-center justify-center p-4">
        <Card className="bg-card w-full max-w-md">
          <CardHeader>
            <CardTitle className="font-literary">Admin Access</CardTitle>
            <CardDescription>Enter your admin key to continue</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Input
                type="password"
                placeholder="Admin Key"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                data-testid="admin-key-input"
              />
              <Button 
                onClick={fetchData} 
                className="w-full bg-cyan-700 hover:bg-cyan-800"
                data-testid="admin-login-btn"
              >
                Access Admin Panel
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0EEEB]">
      <header className="bg-card border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-cyan-300" />
            <span className="font-literary text-xl">Admin Panel</span>
          </div>
          <Button variant="ghost" onClick={() => setAuthenticated(false)}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="reviews" data-testid="admin-reviews-tab">
              Pending Reviews ({reviews.length})
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-messages-tab">
              Contact Messages ({messages.filter(m => !m.read).length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="reviews">
            {reviews.length === 0 ? (
              <div className="text-center py-12">
                <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className="text-muted-foreground">No pending reviews</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map(review => (
                  <Card key={review.id} className="bg-card">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold">{review.title}</h3>
                          <p className="text-sm text-muted-foreground">
                            {review.user_name} ({review.user_email}) - {review.rating} stars
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            onClick={() => handleApprove(review.id)}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`approve-${review.id}`}
                          >
                            <ThumbsUp className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleReject(review.id)}
                            data-testid={`reject-${review.id}`}
                          >
                            <ThumbsDown className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                      <p className="mt-4 text-foreground">{review.content}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(review.created_at).toLocaleString()}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="messages">
            {messages.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-muted-foreground">No messages yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map(msg => (
                  <Card key={msg.id} className={`bg-card ${!msg.read ? 'border-l-4 border-l-cyan-500' : ''}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold">{msg.subject || "No Subject"}</h3>
                          <p className="text-sm text-muted-foreground">
                            {msg.name} ({msg.email})
                          </p>
                        </div>
                        {!msg.read && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleMarkRead(msg.id)}
                          >
                            Mark Read
                          </Button>
                        )}
                      </div>
                      <p className="mt-4 text-foreground">{msg.message}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(msg.created_at).toLocaleString()}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminReviewsPage;
